// src/views/menage/MenageDayDetail.jsx — CHANTIER CALENDRIER RÔLES Lot N5
//
// Détail des ménages d'UN JOUR donné, rendu INLINE sous la grille du calendrier
// Mois (MenageCalendrierView) au clic-jour. Remplace l'ancienne navigation
// (setTab → « Aujourd'hui »/« Suivi équipe ») par un panneau dépliable in-situ.
//
// RÉUTILISATION STRICTE (0 endpoint neuf, 0 migration, 0 RLS) :
//   - fetch menage_runs = COPIE EXACTE de MenageTodayView.reload (mais borné à
//     `dayIso` au lieu de `viewDate`), RLS-scopé `.in(visibleEmails)`. La RLS
//     (agent = ses runs ; orchestrateur = pool ∧ logIds) reste l'arbitre serveur.
//   - RunCard (grammaire de carte unique) rend chaque ménage : accès, codes,
//     horaires théoriques, heures réelles voyageur (orchestrateur ADR-226),
//     réassignation, checklist, signalement d'incident.
//   - handleReassign = UPDATE mono-colonne + rowcount-check (copie MenageTodayView).
//   - ChecklistRunModal / IncidentModal montés tels quels (1 seul à la fois —
//     anti double-drain photo).
//
// FRONTIÈRE : ce composant fait son PROPRE fetch complet (`select('*')`). Le grid
// Mois garde son SELECT minimal `id,logement_id,scheduled_date,status` (frontière
// anti-PII inchangée). Aucune donnée sensible ne transite vers le cœur du grid.
//
// isOwnRun (run.assigned_provider_email === currentUser.email) pilote actif vs
// lecture seule via les props déjà comprises par RunCard/ChecklistRunModal (C2).

import { useState, useEffect, useMemo, useCallback } from "react"
import { supabase } from "../../supabase"
import { DS } from "../../lib/ui"
import { RunCard } from "./RunCard"
import {
  computeVisibleEmails,
  computeAssignOptions,
  buildRunsProviderFilter,
  todayParisIso,
  nowParisHHMM,
} from "../MenageTodayView"
// CHANTIER 42 Phase 4 (ADR-302) — consignes internes : compte par logement pour
// la pastille de carte (helper PARTAGÉ avec MenageTodayView, 1 requête/jour).
import { fetchMyInstructions, countByLogement } from "../../lib/menage-instructions-api"
import { STATUS, createIncident } from "../../lib/incidents-api"
import IncidentModal from "../../modals/IncidentModal"
import ChecklistRunModal from "../../modals/ChecklistRunModal"
// Icônes trait fin (lucide-react direct — DESIGN.md §5, PAS via lib/Icon.jsx).
import { Sun, CircleAlert, CircleCheck, AlertTriangle } from "lucide-react"

// Résout le NOM lisible d'un logement depuis le cfg (props[].logs[] + chambres[]).
// G8 — sert de repli au NOM dans RunCard quand l'endpoint infos est indisponible
// (accès bloqué), pour ne JAMAIS retomber sur un code brut (« lc2 ») dans le
// détail. `null` si introuvable → RunCard retombe alors sur l'id brut (dernier
// recours). Pur, sans réseau, sans PII.
export function resolveLogementNameFromCfg(cfg, logementId) {
  if (!cfg || !logementId) return null
  for (const prop of cfg.props || []) {
    for (const log of prop.logs || []) {
      if (log.id === logementId) return log.nom || null
      for (const ch of log.chambres || []) {
        if (ch.id === logementId) return ch.nom || null
      }
    }
  }
  return null
}

export default function MenageDayDetail({ dayIso, currentUser, cfg, onChanged }) {
  const [runs, setRuns] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [incidentModal, setIncidentModal] = useState(null) // { run, effectiveRunStatus }
  const [checklistModal, setChecklistModal] = useState(null) // { run }
  const [reassignMsg, setReassignMsg] = useState(null)

  // today / nowHHMM figés au mount (cohérent MenageTodayView) — RunCard s'en sert
  // pour le statut effectif + le verrou d'activation. Un refresh recalcule.
  const today = useMemo(() => todayParisIso(), [])
  const nowHHMM = useMemo(() => nowParisHHMM(), [])

  // Périmètre RLS-scopé : self ; orchestrateur = self + managed_agents dédupliqués.
  const visibleEmails = useMemo(() => computeVisibleEmails(currentUser), [currentUser])
  const assignOptions = useMemo(() => computeAssignOptions(currentUser), [currentUser])
  const isOrchestrateur = currentUser?.role === 'orchestrateur'

  // Heures RÉELLES voyageur, ORCHESTRATEUR ONLY (2e fetch endpoint dédié,
  // cloisonné pool ∧ logIds côté serveur). Indexé par run_id. AUCUN cache
  // module-level ; reset dur au changement de compte (anti-rémanence multi-comptes).
  const [guestTimes, setGuestTimes] = useState({})
  useEffect(() => { setGuestTimes({}) }, [currentUser?.email])

  // ── Fetch des runs du jour (COPIE EXACTE de MenageTodayView.reload, borné dayIso) ──
  const reload = useCallback(async () => {
    if (!visibleEmails || visibleEmails.length === 0) { setRuns([]); setLoading(false); return }
    setLoading(true); setError(null)
    try {
      // ORPHELINS (B2) : filtre partagé — menage = .in() inchangé ; orchestrateur
      // = pool + runs sans agent (.or is.null). AFFICHAGE seul, la RLS fait foi.
      const { data: runData, error: rErr } = await buildRunsProviderFilter(
        supabase.from('menage_runs').select('*'),
        currentUser,
        visibleEmails
      )
        .eq('scheduled_date', dayIso)
        .order('id', { ascending: true })
      if (rErr) throw rErr
      setRuns(runData || [])
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }, [dayIso, currentUser, visibleEmails])

  useEffect(() => { reload() }, [reload])

  // 2e fetch : heures réelles pour les runs affichés (orchestrateur uniquement).
  useEffect(() => {
    if (!isOrchestrateur) { setGuestTimes({}); return }
    const runIds = runs.map(r => r.id).filter(Boolean)
    if (runIds.length === 0) { setGuestTimes({}); return }
    let cancelled = false
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) return
        const res = await fetch('/api/beds24-mapping', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ action: 'orchestrateur_guest_times', run_id: runIds }),
        })
        if (!res.ok) return // 403/500 → on garde le théorique seul (pas de fuite)
        const json = await res.json().catch(() => ({}))
        if (!cancelled && json && typeof json === 'object') setGuestTimes(json)
      } catch (_) {
        // silencieux : l'absence de réel laisse simplement la ligne dédiée masquée.
      }
    })()
    return () => { cancelled = true }
  }, [isOrchestrateur, runs, currentUser?.email])

  // ── ADR-302 — consignes internes : compte par logement (pastille de carte).
  // Une seule requête RLS-scopée par jour affiché. Fail-closed : migration
  // absente → {} → aucune pastille, aucun bandeau (inertie agent, leçon R5).
  const [instructionCounts, setInstructionCounts] = useState({})
  useEffect(() => { setInstructionCounts({}) }, [currentUser?.email])
  useEffect(() => {
    const logIds = [...new Set(runs.map(r => r.logement_id).filter(Boolean))]
    if (logIds.length === 0) { setInstructionCounts({}); return }
    let cancelled = false
    fetchMyInstructions(logIds).then(({ instructions }) => {
      if (!cancelled) setInstructionCounts(countByLogement(instructions, logIds))
    })
    return () => { cancelled = true }
  }, [runs, currentUser?.email])

  // ── RÉASSIGNATION (copie MenageTodayView) — UPDATE MONO-COLONNE + ROWCOUNT-CHECK.
  // La RLS orchestrateur_assign refuse en renvoyant 0 ligne SANS erreur (hors
  // pool/scope, >48h, completed) ; le trigger refuse avec une erreur. Les deux
  // cas → message d'échec. On reload le détail ET on propage `onChanged` pour que
  // la grille Mois (badges/compteurs) se rafraîchisse.
  const handleReassign = useCallback(async (run, newEmail) => {
    setReassignMsg(null)
    if (!newEmail || newEmail === run.assigned_provider_email) return
    try {
      const { data, error: e } = await supabase
        .from('menage_runs')
        .update({ assigned_provider_email: newEmail })
        .eq('id', run.id)
        .select('id')
      if (e || !data || data.length === 0) {
        setReassignMsg('❌ Réassignation refusée (hors pool / hors périmètre / run verrouillé ou terminé).')
        return
      }
      setReassignMsg('✅ Ménage réassigné.')
      await reload()
      onChanged?.()
    } catch (e) {
      console.error('[reassign] échec', e?.message || e)
      setReassignMsg('❌ Réassignation refusée (hors pool / hors périmètre / run verrouillé ou terminé).')
    }
  }, [reload, onChanged])

  const handleRunCompleted = useCallback(async () => {
    setChecklistModal(null)
    await reload()
    onChanged?.()
  }, [reload, onChanged])

  const reassignOk = reassignMsg && reassignMsg.startsWith('✅')

  return (
    <div style={{marginTop:14,fontFamily:DS.fontBody,color:DS.marineDark}}>
      {/* Toast post-réassignation (succès/refus). */}
      {reassignMsg && (
        <div style={{marginBottom:12,display:'inline-flex',gap:7,alignItems:'center',padding:'8px 12px',borderRadius:8,fontSize:12,background: reassignOk ? DS.successBg : DS.dangerBg,color: reassignOk ? DS.successText : DS.dangerText}}>
          {reassignOk ? <CircleCheck size={14} strokeWidth={2} /> : <AlertTriangle size={14} strokeWidth={2} />}
          {reassignMsg.replace(/^[✅❌]\s*/, '')}
        </div>
      )}

      {loading ? (
        <div style={{padding:'24px',textAlign:'center',color:DS.textMuted,fontSize:13}}>Chargement du détail…</div>
      ) : error ? (
        <div style={{display:'flex',gap:9,alignItems:'flex-start',padding:14,background:DS.dangerBg,color:DS.dangerText,borderRadius:10,fontSize:13,lineHeight:1.5}}>
          <CircleAlert size={16} strokeWidth={2} style={{flexShrink:0,marginTop:1}} /> {error}
        </div>
      ) : runs.length === 0 ? (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:8,padding:'28px 24px',textAlign:'center',color:DS.textSecondary,fontSize:13,background:DS.card,border:`0.5px solid ${DS.border}`,borderRadius:14}}>
          <span style={{width:42,height:42,borderRadius:12,background:DS.cielSoft,color:DS.marine,display:'inline-flex',alignItems:'center',justifyContent:'center'}}>
            <Sun size={20} strokeWidth={2} />
          </span>
          Aucun ménage ce jour-là.
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {runs.map(run => (
            <RunCard
              key={run.id}
              run={run}
              today={today}
              nowHHMM={nowHHMM}
              // Badge agent affiché UNIQUEMENT pour l'orchestrateur (distingue ses
              // propres runs « Moi » de ceux du pool).
              showAgentBadge={isOrchestrateur}
              isOwnRun={run.assigned_provider_email === currentUser?.email}
              isOrchestrateur={isOrchestrateur}
              guestRealTimes={isOrchestrateur ? (guestTimes[run.id] || null) : null}
              // Repli NOM lisible (cfg) si l'endpoint infos est indisponible —
              // jamais un code brut dans le détail (G8).
              logementNameFallback={resolveLogementNameFromCfg(cfg, run.logement_id)}
              assignOptions={assignOptions}
              // ADR-302 — pastille « N consigne(s) » (0 = aucun rendu).
              instructionsCount={instructionCounts[run.logement_id] || 0}
              onReassign={handleReassign}
              onSignalIncident={(eff) => setIncidentModal({ run, effectiveRunStatus: eff })}
              onOpenChecklist={() => setChecklistModal({ run })}
            />
          ))}
        </div>
      )}

      {/* ── Modals (1 seul à la fois — anti double-drain photo) ── */}
      {incidentModal && (
        <IncidentModal
          incident={null}
          cfg={cfg}
          data={null}
          isAdmin={false}
          userLogIds={[incidentModal.run.logement_id]}
          defaultLogId={incidentModal.run.logement_id}
          effectiveRunStatus={incidentModal.effectiveRunStatus || null}
          run={incidentModal.run}
          onClose={() => setIncidentModal(null)}
          onSave={async (payload) => {
            try {
              await createIncident({
                logement_id: payload.logId,
                run_id: incidentModal.run?.id || null,
                source: 'run',
                reported_by: currentUser?.email,
                voyageur: payload.voyageur || null,
              casse_interne: !!payload.casse_interne,
                incident_date: payload.date || null,
                degradations: payload.degradations || [],
                photos: payload.photos || [],
                label: (payload.degradations?.[0]?.type) || 'Incident multi-dégradations',
                description: (payload.degradations || []).map(d => `${d.type} (${d.nombre || 1}) ${d.precision || ''}`.trim()).join(' · '),
                status: STATUS.EN_COURS,
              })
              setIncidentModal(null)
              await reload()
              onChanged?.()
            } catch (e) {
              // L'erreur est déjà loguée dans createIncident ; on remonte au modal
              // qui affichera le bandeau erreur + reset submitting.
              throw e
            }
          }}
        />
      )}

      {checklistModal && (
        <ChecklistRunModal
          run={checklistModal.run}
          currentUser={currentUser}
          cfg={cfg}
          onClose={() => setChecklistModal(null)}
          onCompleted={handleRunCompleted}
        />
      )}
    </div>
  )
}
